import React, { useState } from 'react';
import { AuthenticatedTemplate } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { ExpandLess, ExpandMore, Description as ContractIcon, Calculate as EstimateIcon } from '@mui/icons-material';
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useAppSelector } from '../../../../../app/hooks';
import { useGetMyProfileQuery } from '../../../../../services/ProfileService';
import { hasContractWriteAccess } from '../../../../profile/utils/profileUtils';

// Types
interface ContractMenuItemConfig {
    id: string;
    title: string;
    icon: React.ComponentType<{ sx?: any }>;
    route: string;
}

// Constants
const MENU_STYLES = {
    navList: { 
        border: 'black solid 1px' 
    },
    nestedListItem: { 
        marginTop: '0px',
        paddingLeft: '48px' // Indent nested items
    },
    icon: { 
        color: 'white' 
    }
} as const;

const CONTRACT_MENU_ITEMS: ContractMenuItemConfig[] = [
    {
        id: 'contract-list',
        title: 'Contract List',
        icon: ContractIcon,
        route: '/contracts'
    },
    {
        id: 'estimate',
        title: 'Estimate',
        icon: EstimateIcon,
        route: '/contracts/estimate'
    }
];

// Sub-components
interface ContractMenuItemProps {
    item: ContractMenuItemConfig;
    onClick: () => void;
}

const ContractMenuItem: React.FC<ContractMenuItemProps> = ({ item, onClick }) => {
    const { title, icon: Icon } = item;

    return (
        <ListItemButton 
            sx={MENU_STYLES.nestedListItem} 
            onClick={onClick}
        >
            <ListItemIcon>
                <Icon sx={MENU_STYLES.icon} />
            </ListItemIcon>
            <ListItemText primary={title} />
        </ListItemButton>
    );
};

// Main component
export const ContractMenu: React.FC = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    
    const selectedCenter = useAppSelector(state => state.center.selectedCenter);
    const { data: profile } = useGetMyProfileQuery();
    const company = profile?.organization || '';

    const handleItemClick = (item: ContractMenuItemConfig): void => {
        const params = new URLSearchParams();
        if (company) params.append('company', company);
        
        const routeWithParams = `${item.route}?${params.toString()}`;
        navigate(routeWithParams);
    };

    const toggleMenu = (): void => setOpen(!open);

    // Only show if user has ContractWrite claim access
    if (!hasContractWriteAccess(profile)) {
        return null;
    }

    return (
        <AuthenticatedTemplate>
            <List component="nav" sx={MENU_STYLES.navList}>
                <ListItemButton onClick={toggleMenu}>
                    <ListItemText primary="Contracts" />
                    {open ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {CONTRACT_MENU_ITEMS.map((item) => (
                        <ContractMenuItem
                            key={item.id}
                            item={item}
                            onClick={() => handleItemClick(item)}
                        />
                    ))}
                </Collapse>
            </List>
        </AuthenticatedTemplate>
    );
};
